import type { LanguageCode, Translations } from '@/i18n/types'

export const defaultLanguage: LanguageCode = 'pt-BR'

export const translations: Record<LanguageCode, Translations> = {
  'pt-BR': {
    meta: {
      title: 'EnvProcess | Desenvolvimento de Sistemas e Consultoria de TI',
      description:
        'Solucoes em Desenvolvimento de Sistemas e Consultoria de TI para impulsionar o crescimento do seu negocio. Desenvolvimento sob medida, integracoes e modernizacao.',
    },
    languageSelector: {
      label: 'Idioma',
      options: [
        { value: 'pt-BR', icon: '/flags/br.svg', label: 'Portugues (Brasil)' },
        { value: 'pt-PT', icon: '/flags/pt.svg', label: 'Portugues (Portugal)' },
        { value: 'en', icon: '/flags/us.svg', label: 'English' },
      ],
    },
    common: {
      contactUs: 'Fale Conosco',
      requestContact: 'Solicitar Contato',
      exploreServices: 'Conhecer Servicos',
      sendMessage: 'Enviar mensagem',
      sending: 'Enviando...',
    },
    header: {
      navAriaLabel: 'Menu principal',
      homeAriaLabel: 'EnvProcess - Inicio',
      openMenu: 'Abrir menu',
      closeMenu: 'Fechar menu',
      nav: {
        inicio: 'Inicio',
        sobre: 'Sobre',
        servicos: 'Servicos',
        diferenciais: 'Diferenciais',
        tecnologias: 'Tecnologias',
        contato: 'Contato',
      },
    },
    hero: {
      badge: 'Desenvolvimento de Sistemas e Consultoria de TI',
      titlePrefix: 'Solucões em TI para',
      titleHighlight: 'impulsionar',
      titleSuffix: 'o crescimento do seu negocio',
      description:
        'Desenvolvimento sob medida, consultoria estrategica, integracoes corporativas e modernizacao de sistemas. Resultados que transformam sua operacao.',
    },
    sobre: {
      title: 'Sobre a EnvProcess',
      subtitle: 'Especialistas em transformacao digital e excelencia em software.',
      paragraphs: [
        'A EnvProcess e uma empresa focada em desenvolvimento de software sob medida, consultoria estrategica em TI e integracao de sistemas. Atuamos como parceiro tecnologico para empresas que buscam modernizar processos, aumentar eficiencia e escalar com seguranca.',
        'Nossa atuacao abrange desenvolvimento de sistemas web, desenho de arquitetura de software, integracoes entre sistemas corporativos, modernizacao e sustentacao de aplicacoes legadas e automacao de processos. Trabalhamos com linguagem corporativa, alinhados aos objetivos de negocio dos nossos clientes.',
        'Combinamos experiencia tecnica, boas praticas e visao de resultado para entregar solucoes que geram impacto real na operacao e na estrategia da sua empresa.',
      ],
    },
    servicos: {
      title: 'Nossos Servicos',
      subtitle:
        'Solucoes completas em desenvolvimento de software e consultoria em TI para sua empresa.',
      items: [
        {
          title: 'Desenvolvimento de Sistemas Web',
          description:
            'Sistemas web sob medida, escalaveis e seguros, alinhados as necessidades do seu negocio e as melhores praticas de mercado.',
        },
        {
          title: 'APIs e Integracoes',
          description:
            'Projetos e implementacao de APIs REST, integracoes entre sistemas corporativos e conectividade com ecossistemas de software.',
        },
        {
          title: 'Consultoria em Arquitetura de Software',
          description:
            'Analise e desenho de arquitetura, definicao de stack e estrategias de evolucao tecnica para suportar o crescimento da empresa.',
        },
        {
          title: 'Sustentacao e Evolucao de Sistemas',
          description:
            'Manutencao continua, correcoes, evolucoes e suporte tecnico para manter seus sistemas estaveis e em constante melhoria.',
        },
        {
          title: 'Modernizacao de Aplicacoes Legadas',
          description:
            'Migracao e modernizacao de sistemas legados para tecnologias atuais, reduzindo debito tecnico e melhorando performance.',
        },
        {
          title: 'Otimizacao de Processos de TI',
          description:
            'Diagnostico e otimizacao de processos de desenvolvimento e operacao de TI, com foco em eficiencia e qualidade.',
        },
      ],
    },
    diferenciais: {
      title: 'Diferenciais',
      subtitle: 'O que nos destaca na entrega de valor para sua empresa.',
      items: [
        {
          title: 'Solucoes personalizadas',
          description: 'Cada projeto e desenhado conforme a realidade e os objetivos do seu negocio.',
        },
        {
          title: 'Arquitetura escalavel',
          description: 'Sistemas preparados para crescer sem comprometer performance ou manutencao.',
        },
        {
          title: 'Foco em performance e seguranca',
          description: 'Boas praticas, codigo seguro e aplicacoes otimizadas desde o desenho.',
        },
        {
          title: 'Experiencia em integracoes corporativas',
          description: 'Conectamos sistemas, APIs e ecossistemas para unificar sua operacao.',
        },
        {
          title: 'Atendimento consultivo',
          description: 'Parceria tecnica com visao de negocio, nao apenas entrega de codigo.',
        },
        {
          title: 'Visao estrategica',
          description: 'Alinhamento entre tecnologia e objetivos empresariais de longo prazo.',
        },
      ],
    },
    tecnologias: {
      title: 'Tecnologias',
      subtitle: 'Stack moderna e comprovada para entregar solucoes robustas e escalaveis.',
    },
    experienciaCorporativa: {
      title: 'Experiencia Corporativa e Ambientes Atendidos',
      subtitle: 'Empresas e ecossistemas presentes na trajetoria combinada dos profissionais.',
      description:
        'A experiencia combinada dos profissionais que compoem o ecossistema de atuacao reune vivencia em empresas e projetos de grande porte, com atuacao em diferentes tecnologias, setores e ambientes corporativos criticos. Os logotipos abaixo representam organizacoes e contextos de negocio presentes nessa trajetoria profissional ampliada.',
      disclaimer:
        'Logotipos apresentados como referencia as empresas e ambientes corporativos presentes na trajetoria profissional combinada da equipe.',
      pillars: [
        'Excelencia Tecnica',
        'Foco no Cliente',
        'Entregas Sustentaveis',
        'Seguranca e Conformidade',
        'Inovacao Continua',
      ],
    },
    porQueEscolher: {
      title: 'Por que escolher a EnvProcess?',
      subtitle: 'Compromisso com resultado, qualidade e parceria de longo prazo.',
      reasons: [
        'Entrega orientada a resultado, com metricas e acompanhamento alinhados ao negocio.',
        'Desenvolvimento com qualidade e boas praticas, reduzindo retrabalho e riscos tecnicos.',
        'Estrutura preparada para crescimento: arquitetura e processos que escalam com voce.',
        'Reducao de retrabalho e ganho de eficiencia em processos e sistemas.',
        'Consultoria com foco em solucao real: tecnologia a servico do seu objetivo, nao o contrario.',
      ],
    },
    contato: {
      title: 'Contato',
      subtitle: 'Envie sua mensagem e retornaremos o mais breve possivel.',
      description:
        'Estamos prontos para entender sua necessidade e apresentar as melhores solucoes em desenvolvimento de sistemas e consultoria em TI.',
      form: {
        labels: {
          name: 'Nome',
          company: 'Empresa',
          email: 'E-mail',
          phone: 'Telefone',
          subject: 'Assunto',
          message: 'Mensagem',
        },
        placeholders: {
          name: 'Seu nome',
          company: 'Nome da empresa',
          email: 'seu@email.com',
          phone: '(11) 99999-9999',
          subject: 'Ex: Orcamento para desenvolvimento',
          message: 'Conte-nos como podemos ajudar...',
        },
        feedback: {
          success: 'Mensagem enviada com sucesso. Em breve entraremos em contato.',
          errorFallback: 'Erro ao enviar. Tente novamente.',
        },
        validation: {
          nameRequired: 'Nome e obrigatorio',
          nameMax: 'Nome deve ter no maximo 150 caracteres',
          companyRequired: 'Empresa e obrigatoria',
          companyMax: 'Empresa deve ter no maximo 150 caracteres',
          emailRequired: 'E-mail e obrigatorio',
          emailInvalid: 'Informe um e-mail valido',
          emailMax: 'E-mail deve ter no maximo 150 caracteres',
          phoneRequired: 'Telefone e obrigatorio',
          phoneInvalid: 'Informe um telefone valido (ex: (11) 99999-9999)',
          phoneMax: 'Telefone deve ter no maximo 30 caracteres',
          subjectRequired: 'Assunto e obrigatorio',
          subjectMax: 'Assunto deve ter no maximo 200 caracteres',
          messageRequired: 'Mensagem e obrigatoria',
          messageMax: 'Mensagem deve ter no maximo 4000 caracteres',
        },
      },
    },
    footer: {
      description:
        'Solucoes em Desenvolvimento de Sistemas e Consultoria de TI para impulsionar o crescimento do seu negocio.',
      quickLinksTitle: 'Links rapidos',
      quickLinks: {
        sobre: 'Sobre',
        servicos: 'Servicos',
        diferenciais: 'Diferenciais',
        tecnologias: 'Tecnologias',
        contato: 'Contato',
      },
      contactTitle: 'Contato',
      copyright: 'Todos os direitos reservados.',
    },
    whatsapp: {
      text: 'Ola! Gostaria de saber mais sobre os servicos da EnvProcess.',
      ariaLabel: 'Abrir conversa no WhatsApp',
    },
    api: {
      networkError:
        'Nao foi possivel enviar sua mensagem no momento. Verifique a configuracao de CORS/proxy da API e tente novamente.',
      genericError: 'Erro ao processar solicitacao.',
    },
  },
  'pt-PT': {
    meta: {
      title: 'EnvProcess | Desenvolvimento de Sistemas e Consultoria de TI',
      description:
        'Solucoes em Desenvolvimento de Sistemas e Consultoria de TI para impulsionar o crescimento do seu negocio. Desenvolvimento a medida, integracoes e modernizacao.',
    },
    languageSelector: {
      label: 'Idioma',
      options: [
        { value: 'pt-BR', icon: '/flags/br.svg', label: 'Portugues (Brasil)' },
        { value: 'pt-PT', icon: '/flags/pt.svg', label: 'Portugues (Portugal)' },
        { value: 'en', icon: '/flags/us.svg', label: 'English' },
      ],
    },
    common: {
      contactUs: 'Fale Connosco',
      requestContact: 'Solicitar Contacto',
      exploreServices: 'Conhecer Servicos',
      sendMessage: 'Enviar mensagem',
      sending: 'A enviar...',
    },
    header: {
      navAriaLabel: 'Menu principal',
      homeAriaLabel: 'EnvProcess - Inicio',
      openMenu: 'Abrir menu',
      closeMenu: 'Fechar menu',
      nav: {
        inicio: 'Inicio',
        sobre: 'Sobre',
        servicos: 'Servicos',
        diferenciais: 'Diferenciadores',
        tecnologias: 'Tecnologias',
        contato: 'Contacto',
      },
    },
    hero: {
      badge: 'Desenvolvimento de Sistemas e Consultoria de TI',
      titlePrefix: 'Solucões em TI para',
      titleHighlight: 'impulsionar',
      titleSuffix: 'o crescimento do seu negocio',
      description:
        'Desenvolvimento a medida, consultoria estrategica, integracoes corporativas e modernizacao de sistemas. Resultados que transformam a sua operacao.',
    },
    sobre: {
      title: 'Sobre a EnvProcess',
      subtitle: 'Especialistas em transformacao digital e excelencia em software.',
      paragraphs: [
        'A EnvProcess e uma empresa focada em desenvolvimento de software a medida, consultoria estrategica em TI e integracao de sistemas. Atuamos como parceiro tecnologico para empresas que procuram modernizar processos, aumentar eficiencia e escalar com seguranca.',
        'A nossa atuacao abrange desenvolvimento de sistemas web, desenho de arquitetura de software, integracoes entre sistemas corporativos, modernizacao e manutencao de aplicacoes legadas e automacao de processos. Trabalhamos com linguagem corporativa, alinhados aos objetivos de negocio dos nossos clientes.',
        'Combinamos experiencia tecnica, boas praticas e visao de resultado para entregar solucoes que geram impacto real na operacao e na estrategia da sua empresa.',
      ],
    },
    servicos: {
      title: 'Os Nossos Servicos',
      subtitle:
        'Solucoes completas em desenvolvimento de software e consultoria em TI para a sua empresa.',
      items: [
        {
          title: 'Desenvolvimento de Sistemas Web',
          description:
            'Sistemas web a medida, escalaveis e seguros, alinhados as necessidades do seu negocio e as melhores praticas do mercado.',
        },
        {
          title: 'APIs e Integracoes',
          description:
            'Projeto e implementacao de APIs REST, integracoes entre sistemas corporativos e conectividade com ecossistemas de software.',
        },
        {
          title: 'Consultoria em Arquitetura de Software',
          description:
            'Analise e desenho de arquitetura, definicao de stack e estrategias de evolucao tecnica para suportar o crescimento da empresa.',
        },
        {
          title: 'Manutencao e Evolucao de Sistemas',
          description:
            'Manutencao continua, correcoes, evolucoes e suporte tecnico para manter os seus sistemas estaveis e em melhoria continua.',
        },
        {
          title: 'Modernizacao de Aplicacoes Legadas',
          description:
            'Migracao e modernizacao de sistemas legados para tecnologias atuais, reduzindo divida tecnica e melhorando desempenho.',
        },
        {
          title: 'Otimizacao de Processos de TI',
          description:
            'Diagnostico e otimizacao de processos de desenvolvimento e operacao de TI, com foco na eficiencia e qualidade.',
        },
      ],
    },
    diferenciais: {
      title: 'Diferenciadores',
      subtitle: 'O que nos destaca na entrega de valor para a sua empresa.',
      items: [
        {
          title: 'Solucoes personalizadas',
          description: 'Cada projeto e desenhado de acordo com a realidade e os objetivos do seu negocio.',
        },
        {
          title: 'Arquitetura escalavel',
          description: 'Sistemas preparados para crescer sem comprometer desempenho ou manutencao.',
        },
        {
          title: 'Foco em desempenho e seguranca',
          description: 'Boas praticas, codigo seguro e aplicacoes otimizadas desde o desenho.',
        },
        {
          title: 'Experiencia em integracoes corporativas',
          description: 'Ligamos sistemas, APIs e ecossistemas para unificar a sua operacao.',
        },
        {
          title: 'Acompanhamento consultivo',
          description: 'Parceria tecnica com visao de negocio, nao apenas entrega de codigo.',
        },
        {
          title: 'Visao estrategica',
          description: 'Alinhamento entre tecnologia e objetivos empresariais de longo prazo.',
        },
      ],
    },
    tecnologias: {
      title: 'Tecnologias',
      subtitle: 'Stack moderna e comprovada para entregar solucoes robustas e escalaveis.',
    },
    experienciaCorporativa: {
      title: 'Experiencia Corporativa e Ambientes Atendidos',
      subtitle: 'Empresas e ecossistemas presentes na trajetoria combinada dos profissionais.',
      description:
        'A experiencia combinada dos profissionais que compoem o ecossistema de atuacao reune vivencia em empresas e projetos de grande porte, com atuacao em diferentes tecnologias, setores e ambientes corporativos criticos. Os logotipos abaixo representam organizacoes e contextos de negocio presentes nessa trajetoria profissional ampliada.',
      disclaimer:
        'Logotipos apresentados como referencia as empresas e ambientes corporativos presentes na trajetoria profissional combinada da equipa.',
      pillars: [
        'Excelencia Tecnica',
        'Foco no Cliente',
        'Entregas Sustentaveis',
        'Seguranca e Conformidade',
        'Inovacao Continua',
      ],
    },
    porQueEscolher: {
      title: 'Porque escolher a EnvProcess?',
      subtitle: 'Compromisso com resultado, qualidade e parceria de longo prazo.',
      reasons: [
        'Entrega orientada a resultados, com metricas e acompanhamento alinhados ao negocio.',
        'Desenvolvimento com qualidade e boas praticas, reduzindo retrabalho e riscos tecnicos.',
        'Estrutura preparada para crescimento: arquitetura e processos que escalam consigo.',
        'Reducao de retrabalho e ganho de eficiencia em processos e sistemas.',
        'Consultoria com foco em solucao real: tecnologia ao servico do seu objetivo, nao o contrario.',
      ],
    },
    contato: {
      title: 'Contacto',
      subtitle: 'Envie a sua mensagem e responderemos com a maior brevidade possivel.',
      description:
        'Estamos prontos para compreender a sua necessidade e apresentar as melhores solucoes em desenvolvimento de sistemas e consultoria em TI.',
      form: {
        labels: {
          name: 'Nome',
          company: 'Empresa',
          email: 'E-mail',
          phone: 'Telefone',
          subject: 'Assunto',
          message: 'Mensagem',
        },
        placeholders: {
          name: 'O seu nome',
          company: 'Nome da empresa',
          email: 'seu@email.com',
          phone: '(11) 99999-9999',
          subject: 'Ex: Orcamento para desenvolvimento',
          message: 'Conte-nos como podemos ajudar...',
        },
        feedback: {
          success: 'Mensagem enviada com sucesso. Em breve entraremos em contacto.',
          errorFallback: 'Erro ao enviar. Tente novamente.',
        },
        validation: {
          nameRequired: 'Nome e obrigatorio',
          nameMax: 'Nome deve ter no maximo 150 caracteres',
          companyRequired: 'Empresa e obrigatoria',
          companyMax: 'Empresa deve ter no maximo 150 caracteres',
          emailRequired: 'E-mail e obrigatorio',
          emailInvalid: 'Indique um e-mail valido',
          emailMax: 'E-mail deve ter no maximo 150 caracteres',
          phoneRequired: 'Telefone e obrigatorio',
          phoneInvalid: 'Indique um telefone valido (ex: (11) 99999-9999)',
          phoneMax: 'Telefone deve ter no maximo 30 caracteres',
          subjectRequired: 'Assunto e obrigatorio',
          subjectMax: 'Assunto deve ter no maximo 200 caracteres',
          messageRequired: 'Mensagem e obrigatoria',
          messageMax: 'Mensagem deve ter no maximo 4000 caracteres',
        },
      },
    },
    footer: {
      description:
        'Solucões em Desenvolvimento de Sistemas e Consultoria de TI para impulsionar o crescimento do seu negocio.',
      quickLinksTitle: 'Links rapidos',
      quickLinks: {
        sobre: 'Sobre',
        servicos: 'Servicos',
        diferenciais: 'Diferenciadores',
        tecnologias: 'Tecnologias',
        contato: 'Contacto',
      },
      contactTitle: 'Contacto',
      copyright: 'Todos os direitos reservados.',
    },
    whatsapp: {
      text: 'Ola! Gostaria de saber mais sobre os servicos da EnvProcess.',
      ariaLabel: 'Abrir conversa no WhatsApp',
    },
    api: {
      networkError:
        'Nao foi possivel enviar a sua mensagem neste momento. Verifique a configuracao de CORS/proxy da API e tente novamente.',
      genericError: 'Erro ao processar pedido.',
    },
  },
  en: {
    meta: {
      title: 'EnvProcess | Software Development and IT Consulting',
      description:
        'Software development and IT consulting solutions to accelerate business growth. Custom development, integrations, modernization, and strategic consulting.',
    },
    languageSelector: {
      label: 'Language',
      options: [
        { value: 'pt-BR', icon: '/flags/br.svg', label: 'Portugues (Brasil)' },
        { value: 'pt-PT', icon: '/flags/pt.svg', label: 'Portugues (Portugal)' },
        { value: 'en', icon: '/flags/us.svg', label: 'English' },
      ],
    },
    common: {
      contactUs: 'Contact Us',
      requestContact: 'Request Contact',
      exploreServices: 'Explore Services',
      sendMessage: 'Send message',
      sending: 'Sending...',
    },
    header: {
      navAriaLabel: 'Main menu',
      homeAriaLabel: 'EnvProcess - Home',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      nav: {
        inicio: 'Home',
        sobre: 'About',
        servicos: 'Services',
        diferenciais: 'Highlights',
        tecnologias: 'Technologies',
        contato: 'Contact',
      },
    },
    hero: {
      badge: 'Software Development and IT Consulting',
      titlePrefix: 'IT solutions to',
      titleHighlight: 'accelerate',
      titleSuffix: 'your business growth',
      description:
        'Tailored software development, strategic consulting, enterprise integrations, and system modernization. Results that transform your operations.',
    },
    sobre: {
      title: 'About EnvProcess',
      subtitle: 'Specialists in digital transformation and software excellence.',
      paragraphs: [
        'EnvProcess is a company focused on custom software development, strategic IT consulting, and system integration. We act as a technology partner for organizations that want to modernize processes, improve efficiency, and scale safely.',
        'Our work includes web systems development, software architecture design, enterprise integrations, modernization and maintenance of legacy applications, and process automation. We work in alignment with each client business goals.',
        'We combine technical expertise, best practices, and a results-driven mindset to deliver solutions that create real operational and strategic impact.',
      ],
    },
    servicos: {
      title: 'Our Services',
      subtitle: 'Complete software development and IT consulting solutions for your company.',
      items: [
        {
          title: 'Web System Development',
          description:
            'Custom web systems that are scalable and secure, aligned with your business needs and market best practices.',
        },
        {
          title: 'APIs and Integrations',
          description:
            'REST API projects and implementation, enterprise integrations, and connectivity across software ecosystems.',
        },
        {
          title: 'Software Architecture Consulting',
          description:
            'Architecture assessment and design, stack definition, and technical evolution strategies to support company growth.',
        },
        {
          title: 'System Support and Evolution',
          description:
            'Ongoing maintenance, fixes, improvements, and technical support to keep your systems stable and improving.',
        },
        {
          title: 'Legacy Application Modernization',
          description:
            'Migration and modernization of legacy systems using current technologies to reduce technical debt and improve performance.',
        },
        {
          title: 'IT Process Optimization',
          description:
            'Assessment and optimization of development and IT operations processes with a focus on quality and efficiency.',
        },
      ],
    },
    diferenciais: {
      title: 'Highlights',
      subtitle: 'What sets us apart when delivering value to your business.',
      items: [
        {
          title: 'Tailored solutions',
          description: 'Every project is designed around your business reality and goals.',
        },
        {
          title: 'Scalable architecture',
          description: 'Systems built to grow without compromising performance or maintainability.',
        },
        {
          title: 'Performance and security focus',
          description: 'Best practices, secure code, and optimized applications from the start.',
        },
        {
          title: 'Enterprise integration expertise',
          description: 'We connect systems, APIs, and ecosystems to unify your operations.',
        },
        {
          title: 'Consultative engagement',
          description: 'A technical partnership with business vision, not just code delivery.',
        },
        {
          title: 'Strategic vision',
          description: 'Technology aligned with long-term business goals.',
        },
      ],
    },
    tecnologias: {
      title: 'Technologies',
      subtitle: 'A modern, proven stack to deliver robust and scalable solutions.',
    },
    experienciaCorporativa: {
      title: 'Corporate Experience and Served Environments',
      subtitle: 'Companies and ecosystems present in the combined professional journey.',
      description:
        'The combined experience of the professionals who make up the operating ecosystem brings expertise in large companies and projects, working across different technologies, sectors, and critical corporate environments. The logos below represent organizations and business contexts present in this broader professional journey.',
      disclaimer:
        'Logos shown as a reference to the companies and corporate environments present in the team\'s combined professional journey.',
      pillars: [
        'Technical Excellence',
        'Customer Focus',
        'Sustainable Delivery',
        'Security and Compliance',
        'Continuous Innovation',
      ],
    },
    porQueEscolher: {
      title: 'Why choose EnvProcess?',
      subtitle: 'Commitment to results, quality, and long-term partnership.',
      reasons: [
        'Results-oriented delivery with metrics and follow-up aligned with business goals.',
        'Quality-driven development and best practices that reduce rework and technical risks.',
        'Growth-ready structure: architecture and processes that scale with your business.',
        'Less rework and greater efficiency across processes and systems.',
        'Consulting focused on real solutions: technology serving your goals, not the other way around.',
      ],
    },
    contato: {
      title: 'Contact',
      subtitle: 'Send your message and we will get back to you as soon as possible.',
      description:
        'We are ready to understand your needs and present the best software development and IT consulting solutions.',
      form: {
        labels: {
          name: 'Name',
          company: 'Company',
          email: 'Email',
          phone: 'Phone',
          subject: 'Subject',
          message: 'Message',
        },
        placeholders: {
          name: 'Your name',
          company: 'Company name',
          email: 'you@email.com',
          phone: '(11) 99999-9999',
          subject: 'Ex: Quote for software development',
          message: 'Tell us how we can help...',
        },
        feedback: {
          success: 'Message sent successfully. We will contact you shortly.',
          errorFallback: 'Error while sending. Please try again.',
        },
        validation: {
          nameRequired: 'Name is required',
          nameMax: 'Name must have at most 150 characters',
          companyRequired: 'Company is required',
          companyMax: 'Company must have at most 150 characters',
          emailRequired: 'Email is required',
          emailInvalid: 'Enter a valid email',
          emailMax: 'Email must have at most 150 characters',
          phoneRequired: 'Phone is required',
          phoneInvalid: 'Enter a valid phone number (ex: (11) 99999-9999)',
          phoneMax: 'Phone must have at most 30 characters',
          subjectRequired: 'Subject is required',
          subjectMax: 'Subject must have at most 200 characters',
          messageRequired: 'Message is required',
          messageMax: 'Message must have at most 4000 characters',
        },
      },
    },
    footer: {
      description:
        'Software Development and IT Consulting solutions to accelerate your business growth.',
      quickLinksTitle: 'Quick links',
      quickLinks: {
        sobre: 'About',
        servicos: 'Services',
        diferenciais: 'Highlights',
        tecnologias: 'Technologies',
        contato: 'Contact',
      },
      contactTitle: 'Contact',
      copyright: 'All rights reserved.',
    },
    whatsapp: {
      text: 'Hello! I would like to learn more about EnvProcess services.',
      ariaLabel: 'Open WhatsApp chat',
    },
    api: {
      networkError:
        'Unable to send your message right now. Check your API CORS/proxy configuration and try again.',
      genericError: 'Error while processing request.',
    },
  },
}
